import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { ChildrenService } from '@/shared/api/rest/services/children.service'
import { ChildRecord } from '@/shared/api/rest/types'

const LAST_CHECKIN_STALE_MS = 5 * 60_000

/** One row of a child's check-in history, narrowed to what a summary needs. */
type CheckinRow = {
    checkedInDate?: string | null
    checkedOutDate?: string | null
    checkedOut?: boolean
    session?: string | null
}

/** The most recent visit for one child, or `null` when there has never been one. */
export type LastCheckin = {
    checkedInDate?: string | null
    checkedOutDate?: string | null
    checkedOut?: boolean
    session?: string | null
} | null

export type LastCheckinMap = Record<string, LastCheckin | undefined>

/** The latest row by check-in time. The list arrives in no guaranteed order. */
const latestOf = (checkins: CheckinRow[]): LastCheckin => {
    let latest: CheckinRow | null = null
    let latestTime = -Infinity
    for (const row of checkins) {
        const time = row?.checkedInDate ? new Date(row.checkedInDate).getTime() : NaN
        if (Number.isNaN(time) || time <= latestTime) continue
        latestTime = time
        latest = row
    }
    if (!latest) return null
    return {
        checkedInDate: latest.checkedInDate ?? null,
        checkedOutDate: latest.checkedOutDate ?? null,
        checkedOut: !!latest.checkedOut,
        session: latest.session ?? null
    }
}

/**
 * The latest check-in for each of a handful of children, keyed by child id.
 *
 * Which of two duplicate records is the live one is only half-answered by the
 * parent's last login: the children are what the merge actually moves, and a
 * record whose children last came to church two years ago is a different
 * proposition from one with a child in the building today. This is read from
 * the child's own history and has nothing to do with the parent account.
 *
 * `GET /children/:id` is the source because it already eager-loads `checkins`
 * and is guarded by `JwtAuthGuard` alone, so a parent reviewing their own
 * duplicate sees the same answer a teacher does. The check-in list endpoint is
 * the wrong tool here twice over: it is teacher-only, and it can only filter by
 * `qrCode` — which is the scan payload, NOT what `children.qrCode` holds (that
 * column keeps the rendered QR image), so it silently matches nothing. A child
 * checked in without a QR code has no code to match on at all.
 *
 * Call this with the children of a single open pair, never a whole list: it is
 * one request per child.
 */
export const useChildLastCheckins = (children: ChildRecord[]) => {
    const childIds = useMemo(
        () => children.map((child) => child._id).filter(Boolean),
        [children]
    )

    const queries = useQueries({
        queries: childIds.map((childId) => ({
            queryKey: ['child-last-checkin', childId],
            queryFn: async (): Promise<LastCheckin> => {
                const child = (await ChildrenService.findById(childId)) as ChildRecord & {
                    checkins?: CheckinRow[]
                }
                return latestOf(child?.checkins ?? [])
            },
            staleTime: LAST_CHECKIN_STALE_MS,
            // A missing history is a detail on a row, not a reason to retry a
            // request nobody is waiting on.
            retry: false
        }))
    })

    // `useQueries` hands back a new array every render; the fetch timestamps
    // only move when data actually lands.
    const version = queries.map((query) => query.dataUpdatedAt).join(',')

    const lastCheckins = useMemo<LastCheckinMap>(() => {
        const map: LastCheckinMap = {}
        childIds.forEach((childId, index) => {
            const query = queries[index]
            if (query && !query.isLoading && !query.error) map[childId] = query.data
        })
        return map
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childIds, version])

    return {
        lastCheckins,
        /** Whether the column has anything to say yet — false before any child resolves. */
        available: childIds.length > 0,
        loading: queries.some((query) => query.isLoading)
    }
}
